import React, { Component } from 'react';
import Radium from 'radium';
import _ from 'underscore';
import bind from 'bind-decorator';
import PropTypes from 'prop-types';
import {
  Dropdown, Modal, Button, Progress,
} from 'semantic-ui-react';
import { Link } from 'react-router-dom';
import moment from 'moment';
import pluralize from 'pluralize';
import Group from '@/models/group';
import CourseSchedule from '@/models/course_schedule';
import User from '@/models/user';
import ModelTable from '../Shared/ModelTable';
import { Features, GROUP_TYPE_COMPLIANCE, GROUP_TYPE_LEARNING } from '../../constants';
import CourseScheduler from '../Shared/CourseScheduler';

class CourseGroups extends Component {
  static propTypes = {
    course: PropTypes.object,
  };

  constructor(props) {
    super(props);

    this.state = {
      courseSchedules: [],
      selectedGroups: [],
      groupIdsToAssign: [],
      showRemovalConfirmation: false,
      pendingSchedule: {},
      showProgress: false,
      progressPercent: 0,
    };
  }

  async componentDidMount() {
    const me = await User.me();
    this.setCourseSchedules();
    this.setState({
      me,
    });
  }

  async setCourseSchedules() {
    const course = this.props.course;
    const courseSchedules = await CourseSchedule.objects().filtered({ course_id: course.id }).all();
    this.setState({
      courseSchedules,
    });
  }

  @bind
  async unassignCourses() {
    this.setState(
      {
        showProgress: true,
        progressPercent: 0,
        showRemovalConfirmation: false,
      },
      () => {
        const total = this.state.selectedGroups.length;
        let completed = 0;

        const actions = _.map(this.state.selectedGroups, (info) => {
          const groupId = info[0];
          const schedule = _.findWhere(this.state.courseSchedules, {
            organizational_group_id: groupId,
          });

          return CourseSchedule.objects()
            .update(schedule.id, { deleted: true })
            .then(() => {
              completed += 1;
              this.setState({ progressPercent: (completed / total) * 100.0 });
            });
        });
        Promise.all(actions).then(() => {
          this.setState({
            showProgress: false,
            selectedGroups: [],
          });
          this.modelTableUpdateMethod();
          this.setCourseSchedules();
        });
      },
    );
  }

  @bind
  confirmCourseRemoval(groupIds) {
    if (!groupIds || groupIds.length === 0) {
      return;
    }
    this.setState({
      showRemovalConfirmation: true,
      selectedGroups: groupIds,
    });
  }

  @bind
  assignCourse() {
    this.setState({
      showGroupAssignment: true,
      assignmentStep: 1,
      groupIdsToAssign: [],
    });
  }

  @bind
  getUpdateHandler(modelTableUpdateMethod) {
    this.modelTableUpdateMethod = modelTableUpdateMethod;
  }

  @bind
  assignGroupsChanged(groups) {
    const groupIds = _.map(groups, (group) => group.id);
    this.setState({
      groupIdsToAssign: groupIds,
    });
  }

  @bind
  onScheduleChange(pendingSchedule) {
    this.setState({ pendingSchedule });
  }

  @bind
  async scheduleCourse() {
    const { course } = this.props;
    const schedule = this.state.pendingSchedule;

    this.setState(
      {
        showGroupAssignment: false,
        showProgress: true,
        progressPercent: 0,
      },
      () => {
        const total = this.state.groupIdsToAssign.length;
        let completed = 0;
        const actions = _.map(this.state.groupIdsToAssign, (groupId) => {
          const scheduleData = {
            organizational_group_id: groupId,
            course_id: course.id,
            start_date: schedule.start_date,
            start_date_tz: schedule.start_date_tz,
            review_frequency: schedule.review_frequency,
            review_frequency_type: schedule.review_frequency_type,
            retake_threshold: schedule.retake_threshold,
          };
          return CourseSchedule.objects()
            .create(scheduleData)
            .then(() => {
              completed += 1;
              this.setState({ progressPercent: (completed / total) * 100.0 });
            });
        });
        Promise.all(actions).then(() => {
          this.setState({ showProgress: false }, this.modelTableUpdateMethod);
          this.setCourseSchedules();
        });
      },
    );
  }

  @bind
  selectionChanged(selectedGroups) {
    this.setState({ selectedGroups });
  }

  render() {
    const { course } = this.props;
    const { selectedGroups, me } = this.state;
    const showAdvancedScheduling = me && me.waffle_flags.includes(Features.ADVANCED_SCHEDULING);

    let groupName = '';
    if (selectedGroups.length === 1) {
      groupName = selectedGroups[0][1];
    }
    const groupDescription = selectedGroups.length > 1 ? `${selectedGroups.length} groups` : `the "${groupName}" group`;

    const columnData = [
      {
        name: 'Group Name',
        sort: true,
        search: true,
        key: [
          'organizational_group_id',
          'group__name',
          'group__group_type',
          (id, name, groupType) => {
            if (groupType === GROUP_TYPE_COMPLIANCE) {
              return <Link to="/app/v2/compliance/">{name}</Link>;
            }
            return <Link to={`/app/v2/groups/${id}`}>{name}</Link>;
          },
        ],
      },
      {
        name: 'Course Status',
        sort: true,
        key: [
          'start_date',
          (startDate) => {
            if (startDate) {
              if (moment(startDate).isBefore(moment())) {
                return 'Active';
              }
              return 'Scheduled';
            }
            return 'Available';
          },
        ],
      },
      {
        name: 'Learners',
        sort: true,
        key: 'organizational_group_summary__learner_count',
      },
      {
        name: 'Courses',
        key: 'organizational_group_summary__course_count',
      },
      {
        name: 'Actions',
        key: [
          'organizational_group_id',
          'group__name',
          (groupId, name) => (
            <Dropdown icon="ellipsis vertical" floating labeled className="icon">
              <Dropdown.Menu>
                <Dropdown.Item
                  text="Remove from Group"
                  onClick={() => {
                    this.confirmCourseRemoval([[groupId, name]]);
                  }}
                />
              </Dropdown.Menu>
            </Dropdown>
          ),
        ],
      },
    ];
    return (
      <div>
        <ModelTable
          title="Groups"
          model={CourseSchedule}
          join={[
            'group__name',
            'group__group_type',
            'group__deleted',
            'organizational_group_summary__learner_count',
            'organizational_group_summary__course_count',
          ]}
          columns={columnData}
          defaultSort={['group__name', 'dsc']}
          select
          baseFilter={{ course_id: course.id, deleted: false, group__deleted: false }}
          updateHandler={this.getUpdateHandler}
          selectionChanged={this.selectionChanged}
        >
          <ModelTable.Actions>
            <ModelTable.Action
              name={`Remove from ${pluralize('Group', selectedGroups.length)}`}
              method={(objs) => {
                const ids = objs.map((obj) => [obj.organizational_group_id, obj.group__name]);
                this.confirmCourseRemoval(ids);
              }}
              requireSelected
            />
            <ModelTable.Action
              name="Add to Group(s)"
              method={this.assignCourse}
            />
          </ModelTable.Actions>
        </ModelTable>

        <Modal open={this.state.showRemovalConfirmation}>
          <Modal.Header>Confirm Action</Modal.Header>
          <Modal.Content image>
            <Modal.Description>
              <p>
                Are you sure you wish to remove the course
                {' '}
                <b>{course.name}</b>
                {' '}
                from
                {' '}
                {groupDescription}
                ?
              </p>
            </Modal.Description>
          </Modal.Content>
          <Modal.Actions>
            <Button
              onClick={() => {
                this.setState({
                  showRemovalConfirmation: false,
                });
              }}
            >
              Cancel
            </Button>
            <Button content="Remove" color="red" onClick={this.unassignCourses} />
          </Modal.Actions>
        </Modal>

        <Modal open={this.state.showGroupAssignment}>
          <Modal.Header>Assign Course</Modal.Header>
          <Modal.Content image>
            {this.state.assignmentStep === 1 ? (
              <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                <p>
                  Please select which group(s) you&apos;d like to schedule this course into, and
                  click &quot;next&quot;
                </p>
                <ModelTable
                  model={Group}
                  columns={[
                    {
                      name: 'Group Name',
                      search: true,
                      key: 'name',
                    },
                  ]}
                  defaultSort={['name', 'asc']}
                  selectionChanged={this.assignGroupsChanged}
                  select
                  baseFilter={{
                    id__not: _.map(this.state.courseSchedules, (s) => s.organizational_group_id),
                    group_type: GROUP_TYPE_LEARNING,
                    deleted: false,
                  }}
                />
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                <CourseScheduler
                  showAdvancedScheduling={showAdvancedScheduling}
                  scheduleCourse={this.scheduleCourse}
                  stopScheduling={() => {}}
                  onScheduleChange={this.onScheduleChange}
                  inModal
                />
              </div>
            )}
          </Modal.Content>
          <Modal.Actions>
            <Button
              content={this.state.assignmentStep === 1 ? 'Cancel' : 'Back'}
              onClick={() => {
                if (this.state.assignmentStep === 1) {
                  this.setState({ showGroupAssignment: false });
                } else {
                  this.setState({
                    assignmentStep: 1,
                    groupIdsToAssign: [],
                  });
                }
              }}
            />
            <Button
              content={this.state.assignmentStep === 1 ? 'Next' : 'Schedule'}
              disabled={this.state.groupIdsToAssign.length === 0}
              color="green"
              onClick={() => {
                if (this.state.assignmentStep === 1) {
                  this.setState({ assignmentStep: 2 });
                } else {
                  this.scheduleCourse();
                }
              }}
            />
          </Modal.Actions>
        </Modal>

        <Modal open={this.state.showProgress}>
          <Modal.Header>Working...</Modal.Header>
          <Modal.Content image>
            <Progress
              percent={this.state.progressPercent}
              indicating
              style={{ margin: '10px', width: '100%' }}
            />
          </Modal.Content>
        </Modal>
      </div>
    );
  }
}

export default Radium(CourseGroups);
